import React, { useState, useEffect, useMemo } from "react";
import {
  FaTimes,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowLeft,
  FaCheck,
  FaAngleRight,
  FaRedo,
  FaPowerOff,
  FaWarehouse,
  FaIndustry,
  FaSearch,
  FaPeopleCarry,
  FaCogs,
  FaBuilding,
} from "react-icons/fa";
import { Checkbox, FormControlLabel } from "@mui/material";

import useAuth from "../../Hooks/useAuth";
import useDrawerTransition from "../../Hooks/useDrawerTransition";
import {
  DEVICE_ACTIVITY_ACTION_CLOSE,
  DEVICE_ACTIVITY_ACTION_OPEN,
  deviceActivityStateLabel,
} from "../../utils/deviceActivityLabels";
import { getFirmTypeStyle } from "../../utils/firmTypeStyles";
import turkishCoffeeMachine from "../../Assets/icons/turkish-coffee-machine.png";
import teaFilterCoffeeMachine from "../../Assets/icons/tea-filter-coffee-machine-icon.png";
import teaTurkishCoffeeMachine from "../../Assets/icons/tea_coffee_machine.png";

const MACHINE_SERVICE_REASONS = {
  COMMON: [
    "Yazılım Güncellemesi",
    "Bakım ve Onarım",
    "Ana Kart Arızası",
    "Ekran Arızası",
    "Kartlar Arası Haberleşme Arızası",
    "Şebeke Pompası Arızası",
    "NTC(Sıcaklık Sensörü) Arızası",
  ],
  TEA_AND_COFFEE: [
    "Röle Kartı Arızası",
    "Isıtıcı(Rezistans) Arızası",
    "Şamandıra Arızası",
    "Demleme Pompası Arızası",
    "Dem Valfi Arızası",
    "Su Valfi Arızası",
    "Loadcell Arızası",
    "Damacana Pompası Arızası",
  ],
  CUP_MACHINE: [
    "Askı Kartı Arızası",
    "Su Deposu Sensörü Arızası",
    "Su Deposu Pompası Arızası",
    "Değirmen Sensörü Arızası",
    "Değirmen Motoru Arızası",
    "Değirmen Kartı Arızası",
    "Askı Sensörü(CNY) Arızası",
    "Servis Motoru Arızası",
    "Fincan Sensörü(CNY) Arızası",
    "Isıtıcı(Fişek Rezistans) Arızası",
    "Karıştırıcı Motoru Arızası",
    "Fan Motoru Arızası",
    "Fincan Su Var/Yok Sensörü Arızası",
    "Fincan Su Pompası Arızası",
  ],
};

const getServiceReasons = (device) => {
  let reasons = [...MACHINE_SERVICE_REASONS.COMMON];
  const deviceType = device?.deviceInfo?.deviceName || device?.deviceName;
  if (deviceType) {
    if (["Çayset", "Çayset-Fincanda", "Fincanda"].includes(deviceType)) {
      reasons = [...reasons, ...MACHINE_SERVICE_REASONS.TEA_AND_COFFEE];
    }
    if (["Fincanda", "Çayset-Fincanda"].includes(deviceType)) {
      reasons = [...reasons, ...MACHINE_SERVICE_REASONS.CUP_MACHINE];
    }
  }
  return reasons;
};

const getDeviceImage = (device) => {
  switch (device?.deviceInfo?.deviceName || device?.deviceName) {
    case "Fincanda": return turkishCoffeeMachine;
    case "Çayset": return teaFilterCoffeeMachine;
    case "Çayset-Fincanda": return teaTurkishCoffeeMachine;
    default: return null;
  }
};

/** Adım başlığı + o adımda ne istendiğinin tek cümlelik açıklaması. */
const StepIntro = ({ title, description }) => (
  <div>
    <h4 className="text-base font-SemiBold text-gray-800">{title}</h4>
    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{description}</p>
  </div>
);

const OPERATION_CONFIG = {
  Depo: {
    title: "Depoya Al",
    iconComponent: FaWarehouse,
    gradient: "from-violet-600 to-purple-700",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-600",
    badgeColor: "bg-violet-50 text-violet-700",
    getDescription: (device) =>
      `"${device?.customName || device?.deviceName}" cihazı depoya alınacaktır. Cihaz kota almaya kapatılacaktır.`,
    confirmText: "Cihaz depoya alınacak ve kota almaya kapatılacaktır.",
  },
  Üretim: {
    title: "Üretime Al",
    iconComponent: FaIndustry,
    gradient: "from-sky-600 to-blue-700",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-600",
    badgeColor: "bg-sky-50 text-sky-700",
    getDescription: (device) =>
      `"${device?.customName || device?.deviceName}" cihazı üretime alınacaktır. Cihaz kota almaya açılacaktır.`,
    confirmText: "Cihaz üretime alınacak ve kota almaya açılacaktır.",
  },
  AktiflikKapat: {
    title: DEVICE_ACTIVITY_ACTION_CLOSE,
    iconComponent: FaPowerOff,
    gradient: "from-red-600 to-rose-700",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    badgeColor: "bg-red-50 text-red-700",
    getDescription: (device) =>
      `"${device?.customName || device?.deviceName}" cihazı kota almaya kapatılacaktır.`,
    confirmText: "Cihaz kota almaya kapatılacak.",
  },
  AktiflikAc: {
    title: DEVICE_ACTIVITY_ACTION_OPEN,
    iconComponent: FaPowerOff,
    gradient: "from-emerald-600 to-green-700",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    badgeColor: "bg-emerald-50 text-emerald-700",
    getDescription: (device) =>
      `"${device?.customName || device?.deviceName}" cihazı kota almaya açılacaktır.`,
    confirmText: "Cihaz kota almaya açılacak.",
  },
  Plasiyer: {
    title: "Plasiyere Ver",
    iconComponent: FaPeopleCarry,
    gradient: "from-amber-500 to-orange-600",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    badgeColor: "bg-amber-50 text-amber-700",
    getDescription: (device, extra) =>
      extra
        ? `"${device?.customName || device?.deviceName}" cihazı "${extra.name} ${extra.lastName}" plasiyerine verilecektir.`
        : `"${device?.customName || device?.deviceName}" cihazı seçilen plasiyere verilecektir.`,
    confirmText: "Cihaz seçilen plasiyere verilecektir.",
  },
  Servis: {
    title: "Servise Al",
    iconComponent: FaCogs,
    gradient: "from-teal-600 to-cyan-700",
    iconBg: "bg-teal-100",
    iconColor: "text-teal-600",
    badgeColor: "bg-teal-50 text-teal-700",
    getDescription: (device) =>
      `"${device?.customName || device?.deviceName}" cihazı servise alınacaktır. Servis nedeni zorunludur.`,
    confirmText: "Cihaz servise alınacaktır.",
  },
  Firma: {
    title: "Firmaya Al",
    iconComponent: FaBuilding,
    gradient: "from-indigo-600 to-blue-700",
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600",
    badgeColor: "bg-indigo-50 text-indigo-700",
    getDescription: (device, extra) =>
      extra
        ? `"${device?.customName || device?.deviceName}" cihazı "${extra.firmName}" firmasına taşınacaktır.`
        : `"${device?.customName || device?.deviceName}" cihazı seçilen firmaya taşınacaktır.`,
    confirmText: "Cihaz seçilen firmaya taşınacaktır.",
  },
};

function OperationDrawer({
  open,
  device: deviceProp,
  operation: operationProp, // "Depo", "Üretim", "AktiflikKapat", "AktiflikAc", "Plasiyer", "Servis", "Firma"
  onClose,
  onConfirm,
  isLoading = false,
  isSuccess = false,
  isError = false,
  errorMessage = "",
  successMessage = "",
  playmakers = [], // Plasiyer operasyonu için plasiyer listesi
  firms = [], // Firma operasyonu için firma listesi
  // Firma operasyonunda hedef türü — bayide "customer" = Alt Firmaya Ver,
  // "dealer" = Alt Bayiye Ver (#247). Depo panelinde yalnız customer.
  firmTarget = "customer",
  // Merkezden bayi adına taşıma (#329, CenterDealerMoveDrawer): "toCustomer"
  // = Bayi Müşterisine Ver (operation "Firma", firms = bayinin müşterileri),
  // "takeBack" = Bayiye Geri Al (operation "Depo", firms = müşterinin
  // bayileri; birden çoksa seçim adımı açılır).
  centerDealerMove = null,
  dealerName = "",
}) {
  const { auth } = useAuth();
  // Kapanış kayması boyunca cihaz + operasyon dondurulur. Çağıran taraf
  // onClose'da ikisini de null'a çekiyordu; operation null olunca başlık ve
  // renkler varsayılan "Depo" konfigüne düşüp drawer ekrandayken değişiyordu.
  const drawerData = useMemo(
    () => ({ device: deviceProp, operation: operationProp }),
    [deviceProp, operationProp]
  );
  const { mounted, entered, data: frozen } = useDrawerTransition(open, drawerData);
  const device = frozen?.device ?? deviceProp;
  const operation = frozen?.operation ?? operationProp;
  const [description, setDescription] = useState("");
  const [connectDuringOperationChoice, setConnectDuringOperation] = useState(true);
  // Bayide makine bağlantısı her zaman zorunlu — tercih sorulmaz (kullanıcı
  // kararı 2026-10-09, #290). Depo panelinde çevrimdışı akış seçilebilir.
  const isDealer = auth?.role === "dealer";
  const connectDuringOperation = isDealer || connectDuringOperationChoice;
  // Adım sihirbazı — adım listesi operasyona göre değişir (bkz. `steps`).
  // 1 tabanlı; her adım tek bir bilgi ister, son iki adım Onay ve Sonuç'tur.
  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState("");
  const [selectedPlaymakerId, setSelectedPlaymakerId] = useState("");
  const [playmakerSearch, setPlaymakerSearch] = useState("");
  const [selectedFirmName, setSelectedFirmName] = useState("");
  const [firmSearch, setFirmSearch] = useState("");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [reasonSearch, setReasonSearch] = useState("");
  const [isActiveOnTransfer, setIsActiveOnTransfer] = useState(false);

  // Bayi taşımaları (#247, sözleşme dealer-device-moves.md): aynı uçlar,
  // bayide farklı ad/açıklama — Alt Firmaya / Alt Bayiye Ver, Bayiye Geri Al,
  // merkez servisine gönderme.
  const toSubDealer = isDealer && operation === "Firma" && firmTarget === "dealer";
  const deviceFirmId = device?.firm?._id ?? device?.firm;
  const isTakeBack =
    isDealer && operation === "Depo" && !!deviceFirmId && String(deviceFirmId) !== String(auth?.firm);
  const config = useMemo(() => {
    const base = OPERATION_CONFIG[operation] || OPERATION_CONFIG.Depo;
    const name = (d) => d?.customName || d?.deviceName;
    if (centerDealerMove === "toCustomer") {
      return {
        ...base,
        title: "Bayi Müşterisine Ver",
        getDescription: (d, extra) =>
          extra
            ? `"${name(d)}" cihazı ${dealerName} bayisinden "${extra.firmName}" müşterisine verilecektir.`
            : `"${name(d)}" cihazı ${dealerName} bayisinin seçilen müşterisine verilecektir.`,
        confirmText: "Cihaz bayinin müşterisine verilecek; cihazdaki kota bayinin havuzuna döner.",
      };
    }
    if (centerDealerMove === "takeBack") {
      return {
        ...base,
        title: "Bayiye Geri Al",
        getDescription: (d, extra) =>
          `"${name(d)}" cihazı ${extra?.firmName || dealerName || "seçilen"} bayisinin deposuna geri alınacaktır. Cihaz kota almaya kapatılacaktır.`,
        confirmText: "Cihaz bayinin deposuna geri alınacak ve kota almaya kapatılacaktır; kotası müşterinin havuzuna döner.",
      };
    }
    if (!isDealer) return base;
    if (operation === "Firma" && toSubDealer) {
      return {
        ...base,
        title: "Alt Bayiye Ver",
        getDescription: (d, extra) =>
          extra
            ? `"${name(d)}" cihazı "${extra.firmName}" alt bayisinin deposuna verilecektir.`
            : `"${name(d)}" cihazı seçilen alt bayinin deposuna verilecektir.`,
        confirmText: "Cihaz alt bayinin deposuna kota almaya kapalı olarak verilecektir.",
      };
    }
    if (operation === "Firma") return { ...base, title: "Alt Firmaya Ver" };
    if (isTakeBack) {
      return {
        ...base,
        title: "Bayiye Geri Al",
        getDescription: (d) =>
          `"${name(d)}" cihazı deponuza geri alınacaktır. Cihaz kota almaya kapatılacaktır.`,
        confirmText: "Cihaz deponuza geri alınacak ve kota almaya kapatılacaktır.",
      };
    }
    if (operation === "Servis") {
      return {
        ...base,
        getDescription: (d) =>
          `"${name(d)}" cihazı merkez servisine gönderilecektir. Servis nedeni zorunludur.`,
        confirmText:
          "Cihaz merkez servisine gönderilecek ve listenizden çıkacak; merkez geri verene kadar görünmez.",
      };
    }
    return base;
  }, [operation, isDealer, toSubDealer, isTakeBack, centerDealerMove, dealerName]);
  const IconComponent = config.iconComponent;
  const deviceImage = getDeviceImage(device);

  // Operasyona göre adım listesi. Seçim gerektirmeyen işlemlerde (Depo, Üretim,
  // aktiflik) seçim adımı hiç oluşmaz — 4 adım kalır.
  const steps = useMemo(() => {
    const list = [{ key: "device", label: "Cihaz" }];
    if (operation === "Plasiyer") list.push({ key: "playmaker", label: "Plasiyer" });
    if (operation === "Firma") list.push({ key: "firm", label: "Firma" });
    // Bayiye Geri Al: müşterinin birden çok bayisi varsa hangisine (#329)
    if (centerDealerMove === "takeBack" && firms.length > 1) list.push({ key: "firm", label: "Bayi" });
    // Servis: neden + açıklama + bağlantı tek adımda istenir, ayrı açıklama
    // adımı açılmaz.
    if (operation === "Servis") list.push({ key: "reason", label: "Servis" });
    else list.push({ key: "note", label: "Açıklama" });
    list.push({ key: "confirm", label: "Onay" });
    list.push({ key: "result", label: "Sonuç" });
    return list;
  }, [operation, centerDealerMove, firms.length]);

  // Adım çubuğunun dolu kısmı — operasyon gradyanının ana rengini izler.
  const stepBarColor = config.gradient.includes("red")
    ? "bg-red-400"
    : config.gradient.includes("emerald")
    ? "bg-emerald-400"
    : config.gradient.includes("violet")
    ? "bg-violet-400"
    : config.gradient.includes("amber")
    ? "bg-amber-400"
    : config.gradient.includes("teal")
    ? "bg-teal-400"
    : config.gradient.includes("indigo")
    ? "bg-indigo-400"
    : "bg-sky-400";

  const stepKey = steps[currentStep - 1]?.key;
  const resultStep = steps.length;

  // Plasiyer
  const selectedPlaymaker = useMemo(
    () => playmakers.find((pm) => pm._id === selectedPlaymakerId),
    [playmakers, selectedPlaymakerId]
  );

  const filteredPlaymakers = useMemo(() => {
    if (!playmakerSearch.trim()) return playmakers;
    const q = playmakerSearch.toLowerCase();
    return playmakers.filter(
      (pm) =>
        `${pm.name} ${pm.lastName}`.toLowerCase().includes(q) ||
        pm.name?.toLowerCase().includes(q) ||
        pm.lastName?.toLowerCase().includes(q)
    );
  }, [playmakers, playmakerSearch]);

  // Firma
  const selectedFirm = useMemo(
    () => firms.find((f) => f.firmName === selectedFirmName),
    [firms, selectedFirmName]
  );

  // Firmaya alma işleminde müşteri ve bayi firmaları (#248; depo hariç);
  // bayi panelinde Alt Firmaya Ver yalnız müşteriler, Alt Bayiye Ver'de
  // bayinin kendisi hariç alt bayiler (#247).
  const customerFirms = useMemo(
    () =>
      toSubDealer
        ? firms.filter((f) => f.firmType === "dealer" && String(f._id) !== String(auth?.firm))
        : isDealer
          ? firms.filter((f) => f.firmType === "customer")
          : firms.filter((f) => f.firmType === "customer" || f.firmType === "dealer"),
    [firms, toSubDealer, isDealer, auth?.firm],
  );

  // Bayiye giden cihaz her zaman kota almaya kapalı düşer — sunucu zorluyor
  // (2026-08-17); kota alma tercihi sorulmaz.
  const toDealerFirm =
    toSubDealer || selectedFirm?.firmType === "dealer" || centerDealerMove === "takeBack";

  const filteredFirms = useMemo(() => {
    const base = operation === "Firma" ? customerFirms : firms;
    if (!firmSearch.trim()) return base;
    const q = firmSearch.toLowerCase();
    return base.filter(
      (f) =>
        f.firmName?.toLowerCase().includes(q) ||
        f.bayserNo?.toLowerCase().includes(q)
    );
  }, [firms, customerFirms, firmSearch, operation]);

  // Servis nedenleri
  const serviceReasons = useMemo(() => getServiceReasons(device), [device]);

  const filteredReasons = useMemo(() => {
    if (!reasonSearch.trim()) return serviceReasons;
    return serviceReasons.filter((r) =>
      r.toLowerCase().includes(reasonSearch.toLowerCase())
    );
  }, [serviceReasons, reasonSearch]);

  const toggleReason = (reason) => {
    setStepError("");
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  // Reset on open
  useEffect(() => {
    if (open) {
      setDescription("");
      setConnectDuringOperation(true);
      setCurrentStep(1);
      setSelectedPlaymakerId("");
      setPlaymakerSearch("");
      setSelectedFirmName("");
      setFirmSearch("");
      setSelectedReasons([]);
      setReasonSearch("");
      setIsActiveOnTransfer(false);
      setStepError("");
    }
  }, [open]);

  // Track operation result
  useEffect(() => {
    if (isLoading && currentStep < resultStep) {
      setCurrentStep(resultStep);
    }
  }, [isLoading, currentStep, resultStep]);

  // Prevent body scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const isProcessing = isLoading;

  const buildPayload = () => {
    const payload = {
      device,
      connect: connectDuringOperation,
      description,
    };
    if (operation === "Plasiyer") {
      payload.selectedPlaymakerId = selectedPlaymakerId;
    }
    if (centerDealerMove === "takeBack") {
      // Tek bayide seçim adımı yok — o bayi
      payload.dealerFirmId = (selectedFirm || firms[0])?._id;
    }
    if (operation === "Firma") {
      payload.firmName = selectedFirmName;
      payload.firmId = selectedFirm?._id;
      // Bayiye giden cihaz her zaman kapalı (sunucu da zorluyor).
      payload.isActive = toDealerFirm ? false : isActiveOnTransfer;
    }
    if (operation === "Servis") {
      // Servise alınan cihaz aktif kalır — bayide de, çünkü bayinin Servise
      // Al'ı merkez servisine gönderir (#247; sunucu zorluyor). Sorulmaz.
      payload.isActive = true;
      // Servis nedenlerini açıklamaya birleştir
      // Format: "Neden1, Neden2. Not cümlesi."
      // Parse: ilk "." öncesi = nedenler (virgülle ayrılmış), sonrası = not
      if (selectedReasons.length > 0) {
        const reasonsSentence = selectedReasons.join(", ") + ".";
        if (description) {
          const trimmedNote = description.trim();
          const capitalizedNote = trimmedNote.charAt(0).toUpperCase() + trimmedNote.slice(1);
          const finalNote = capitalizedNote.endsWith(".") ? capitalizedNote : capitalizedNote + ".";
          payload.description = reasonsSentence + " " + finalNote;
        } else {
          payload.description = reasonsSentence;
        }
      }
    }
    return payload;
  };

  /**
   * Adım geçiş kapısı — o adımın zorunlu bilgisi girilmediyse ilerlemeyi
   * durdurur ve nedenini yazar.
   */
  const validateStep = (key) => {
    if (key === "playmaker" && !selectedPlaymakerId) return "Bir plasiyer seçin.";
    if (key === "firm" && !selectedFirmName) return "Bir firma seçin.";
    // Servis: neden VEYA açıklama zorunlu — ikisi de aynı adımda istenir.
    if (key === "reason" && selectedReasons.length === 0 && !description.trim())
      return "En az bir servis nedeni seçin veya açıklama girin.";
    return "";
  };

  const handleNext = () => {
    const err = validateStep(stepKey);
    setStepError(err);
    if (err) return;
    setCurrentStep((s) => Math.min(resultStep, s + 1));
  };

  const handleBack = () => {
    setStepError("");
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const handleConfirm = () => {
    setCurrentStep(resultStep);
    onConfirm(buildPayload());
  };

  const handleRetry = () => {
    onConfirm(buildPayload());
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  // ─── Adım: Cihaz (bilgi + işlem özeti) ───────────────────────────────────
  const renderDeviceStep = () => (
    <div className="space-y-5">
      <StepIntro
        title={`${config.title} — cihazı doğrulayın`}
        description="İşlem yapılacak cihazın doğru olduğundan emin olun, sonra devam edin."
      />
      {/* Cihaz Bilgi Kartı */}
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-start gap-4">
          {deviceImage && (
            <div className="w-16 h-16 bg-white rounded-xl border border-gray-100 flex items-center justify-center overflow-hidden shadow-sm shrink-0">
              <img src={deviceImage} alt="" className="w-full h-full object-contain p-1.5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-gray-900 truncate">
              {device?.customName || device?.deviceName}
            </h4>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{device?.serialNo}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                device?.isActive ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
              }`}>
                {deviceActivityStateLabel(device?.isActive)}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700">
                {device?.deviceStatusName}
              </span>
              {device?.firm?.firmName && (
                <span className="text-[11px] text-gray-400 truncate">
                  {device.firm.firmName}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* İşlem Açıklaması */}
      <div className={`rounded-xl p-4 ${config.iconBg} bg-opacity-40 border border-opacity-30`}
        style={{ borderColor: 'currentColor' }}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.iconBg} flex items-center justify-center shrink-0`}>
            <IconComponent className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">{config.title}</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              {config.getDescription(
                device,
                operation === "Plasiyer" ? selectedPlaymaker : operation === "Firma" ? selectedFirm : null
              )}
            </p>
          </div>
        </div>
      </div>

    </div>
  );

  // ─── Adım: Plasiyer seçimi ───────────────────────────────────────────────
  const renderPlaymakerStep = () => (
    <div className="space-y-5">
      <StepIntro
        title="Plasiyer seçin"
        description="Cihazın teslim edileceği plasiyeri listeden seçin."
      />
      {(
        <div>
          <label className="block text-sm font-Medium text-gray-700 mb-1.5">
            Plasiyer <span className="text-red-400">*</span>
          </label>
          <div className="relative mb-2">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              value={playmakerSearch}
              onChange={(e) => setPlaymakerSearch(e.target.value)}
              placeholder="Plasiyer ara..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-300 text-sm transition-all bg-gray-50"
            />
          </div>
          <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-xl bg-white divide-y divide-gray-50">
            {filteredPlaymakers.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                Plasiyer bulunamadı
              </div>
            ) : (
              filteredPlaymakers.map((pm) => (
                <button
                  key={pm._id}
                  type="button"
                  onClick={() => setSelectedPlaymakerId(pm._id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all ${
                    selectedPlaymakerId === pm._id
                      ? "bg-amber-50 border-l-[3px] border-l-amber-500"
                      : "hover:bg-gray-50 border-l-[3px] border-l-transparent"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    selectedPlaymakerId === pm._id
                      ? "bg-amber-500 text-white"
                      : "bg-gray-100 text-gray-500"
                  }`}>
                    {pm.name?.charAt(0)}{pm.lastName?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      selectedPlaymakerId === pm._id ? "text-amber-700" : "text-gray-700"
                    }`}>
                      {pm.name} {pm.lastName}
                    </p>
                  </div>
                  {selectedPlaymakerId === pm._id && (
                    <FaCheck className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

    </div>
  );

  // ─── Adım: Firma seçimi ──────────────────────────────────────────────────
  const renderFirmStep = () => (
    <div className="space-y-5">
      <StepIntro
        title={toSubDealer ? "Alt bayi seçin" : centerDealerMove === "takeBack" ? "Bayi seçin" : "Firma seçin"}
        description={
          toSubDealer
            ? "Cihazın verileceği alt bayiyi seçin. Cihaz alt bayinin deposuna kota almaya kapalı olarak düşer."
            : centerDealerMove === "takeBack"
            ? "Müşterinin birden çok bayisi var; cihazın geri alınacağı bayiyi seçin. Cihaz bayinin deposuna kota almaya kapalı olarak düşer."
            : centerDealerMove === "toCustomer"
            ? `${dealerName} bayisinin müşterilerinden birini seçin ve kota alma tercihini belirleyin.`
            : isDealer
              ? "Cihazın taşınacağı müşteri firmasını seçin ve kota alma tercihini belirleyin."
              : "Cihazın taşınacağı müşteri veya bayi firmasını seçin. Bayiye giden cihaz bayinin deposuna kota almaya kapalı olarak düşer."
        }
      />
      {(
        <div>
          <label className="block text-sm font-Medium text-gray-700 mb-1.5">
            Firma <span className="text-red-400">*</span>
          </label>
          <div className="relative mb-2">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              value={firmSearch}
              onChange={(e) => setFirmSearch(e.target.value)}
              placeholder="Firma adı veya bayser no ile ara..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-300 text-sm transition-all bg-gray-50"
            />
          </div>
          <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-xl bg-white divide-y divide-gray-50">
            {filteredFirms.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                Firma bulunamadı
              </div>
            ) : (
              filteredFirms.map((firm) => (
                <button
                  key={firm._id}
                  type="button"
                  onClick={() => setSelectedFirmName(firm.firmName)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all ${
                    selectedFirmName === firm.firmName
                      ? "bg-indigo-50 border-l-[3px] border-l-indigo-500"
                      : "hover:bg-gray-50 border-l-[3px] border-l-transparent"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    selectedFirmName === firm.firmName
                      ? "bg-indigo-500 text-white"
                      : "bg-gray-100 text-gray-500"
                  }`}>
                    {firm.firmName?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      selectedFirmName === firm.firmName ? "text-indigo-700" : "text-gray-700"
                    }`}>
                      {firm.firmName}
                    </p>
                    {firm.bayserNo && (
                      <p className="text-[11px] text-gray-400 font-mono">{firm.bayserNo}</p>
                    )}
                  </div>
                  {!isDealer && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border shrink-0 ${getFirmTypeStyle(firm).badge}`}>
                      {getFirmTypeStyle(firm).label}
                    </span>
                  )}
                  {selectedFirmName === firm.firmName && (
                    <FaCheck className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>

          {/* Firmaya alırken cihazın kota alma durumu (isActive) — bayiye
              giden cihaz her zaman kapalı, sorulmaz. */}
          {!toDealerFirm && (
          <div className="mt-3 rounded-xl border border-gray-200 p-3 bg-gray-50/50">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActiveOnTransfer}
                  onChange={(e) => setIsActiveOnTransfer(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <div>
                  <span className="text-sm text-gray-700 font-medium">
                    Cihaz kota almaya açık olarak alınsın mı?
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    İşaretlenmezse cihaz firmaya kota almaya kapalı olarak alınır.
                  </p>
                </div>
              }
            />
          </div>
          )}
        </div>
      )}

    </div>
  );

  // ─── Adım: Servis nedeni ─────────────────────────────────────────────────
  const renderReasonStep = () => (
    <div className="space-y-5">
      <StepIntro
        title="Servis nedeni seçin"
        description="Cihazın neden servise alındığını işaretleyin. Neden seçmezseniz sonraki adımda açıklama zorunlu olur."
      />
      {(
        <div>
          <label className="block text-sm font-Medium text-gray-700 mb-1.5">
            Servis Nedeni <span className="text-red-400">*</span>
          </label>
          <div className="relative mb-2">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              value={reasonSearch}
              onChange={(e) => setReasonSearch(e.target.value)}
              placeholder="Neden ara..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-300 text-sm transition-all bg-gray-50"
            />
          </div>
          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-xl bg-white divide-y divide-gray-50">
            {filteredReasons.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                Neden bulunamadı
              </div>
            ) : (
              filteredReasons.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => toggleReason(reason)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-all ${
                    selectedReasons.includes(reason)
                      ? "bg-teal-50 border-l-[3px] border-l-teal-500"
                      : "hover:bg-gray-50 border-l-[3px] border-l-transparent"
                  }`}
                >
                  <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 border ${
                    selectedReasons.includes(reason)
                      ? "bg-teal-500 border-teal-500 text-white"
                      : "border-gray-300 bg-white"
                  }`}>
                    {selectedReasons.includes(reason) && <FaCheck className="w-2.5 h-2.5" />}
                  </div>
                  <span className={`text-sm ${
                    selectedReasons.includes(reason) ? "text-teal-700 font-medium" : "text-gray-700"
                  }`}>
                    {reason}
                  </span>
                </button>
              ))
            )}
          </div>
          {selectedReasons.length > 0 && (
            <div className="mt-2 px-3 py-2 bg-teal-50 rounded-lg">
              <span className="text-xs font-Medium text-teal-700">
                {selectedReasons.length} neden seçildi
              </span>
            </div>
          )}
        </div>
      )}

      {/* Servis akışında açıklama ve bağlantı tercihi ayrı adım değil —
          neden seçimiyle aynı ekranda istenir. */}
      {renderDescriptionField()}
      {renderConnectionField()}
    </div>
  );

  // ─── Adım: Açıklama + bağlantı tercihi (servis dışı operasyonlar) ────────
  const renderNoteStep = () => (
    <div className="space-y-5">
      <StepIntro
        title={isDealer ? "Açıklama" : "Açıklama ve bağlantı"}
        description={
          isDealer
            ? "İşlemle ilgili not ekleyin. İşlem sırasında cihaza bağlantı kurulur."
            : "İşlemle ilgili not ekleyin ve cihaza bağlantı kurulup kurulmayacağını seçin."
        }
      />
      {renderDescriptionField()}
      {renderConnectionField()}
    </div>
  );

  // Açıklama alanı — hem servis nedeni adımında hem açıklama adımında kullanılır.
  function renderDescriptionField() {
    return (
      /* Açıklama */
      <div>
        <label className="block text-sm font-Medium text-gray-700 mb-1.5">
          Açıklama {operation === "Servis"
            ? <span className="text-gray-400 font-normal">(servis nedeni veya açıklama zorunlu)</span>
            : <span className="text-gray-400 font-normal">(isteğe bağlı)</span>
          }
        </label>
        <textarea
          className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm resize-none transition-all ${
            operation === "Servis" && selectedReasons.length === 0 && !description
              ? "border-red-300"
              : "border-gray-200"
          }`}
          value={description}
          onChange={(e) => { setDescription(e.target.value); setStepError(""); }}
          placeholder="İşlem hakkında not ekleyebilirsiniz..."
          rows={3}
        />
      </div>
    );
  }

  // Bağlantı tercihi — aynı şekilde iki adımda paylaşılır.
  function renderConnectionField() {
    if (isDealer) return null;
    return (
      /* Bağlantı Tercihi */
      <div className="rounded-xl border border-gray-200 p-3.5 bg-gray-50/50">
        <FormControlLabel
          control={
            <Checkbox
              checked={connectDuringOperation}
              onChange={(e) => setConnectDuringOperation(e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={
            <div>
              <span className="text-sm text-gray-700 font-medium">
                Makine bağlantısı kurulsun mu?
              </span>
              <p className="text-xs text-gray-500 mt-0.5">
                İşlem sırasında cihaza otomatik bağlantı kurulur.
              </p>
            </div>
          }
        />
      </div>
    );
  }

  // ─── Step 2: Onay ────────────────────────────────────────────────────────
  const renderStep2 = () => (
    <div className="flex flex-col items-center py-8">
      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg mb-6`}>
        <IconComponent className="w-10 h-10 text-white" />
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-2">{config.title}</h3>
      <p className="text-sm text-gray-500 text-center max-w-xs mb-8">
        {config.getDescription(
          device,
          operation === "Plasiyer" ? selectedPlaymaker : operation === "Firma" ? selectedFirm : null
        )}
      </p>

      <div className="w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="divide-y divide-gray-100">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-xs font-Medium text-gray-500 tracking-wide">CİHAZ</span>
            <span className="text-sm font-semibold text-gray-900">
              {device?.customName || device?.deviceName}
              <span className="text-xs text-gray-400 ml-1">({device?.serialNo})</span>
            </span>
          </div>
          {device?.firm?.firmName && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide">MEVCUT FİRMA</span>
              <span className="text-sm text-gray-900">{device.firm.firmName}</span>
            </div>
          )}
          {operation === "Plasiyer" && selectedPlaymaker && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide">PLASİYER</span>
              <span className="text-sm font-semibold text-amber-700">
                {selectedPlaymaker.name} {selectedPlaymaker.lastName}
              </span>
            </div>
          )}
          {centerDealerMove === "takeBack" && (selectedFirm || firms[0]) && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide">HEDEF BAYİ</span>
              <span className="text-sm font-semibold text-violet-700">
                {(selectedFirm || firms[0]).firmName}
              </span>
            </div>
          )}
          {operation === "Firma" && selectedFirm && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide">HEDEF FİRMA</span>
              <span className="text-sm font-semibold text-indigo-700">
                {selectedFirm.firmName}
              </span>
            </div>
          )}
          {operation === "Firma" && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide">KOTA ALMA</span>
              <span className={`text-sm font-medium ${isActiveOnTransfer && !toDealerFirm ? "text-emerald-600" : "text-gray-500"}`}>
                {isActiveOnTransfer && !toDealerFirm ? "Kota almaya açık olarak alınacak" : "Kota almaya kapalı olarak alınacak"}
              </span>
            </div>
          )}
          {/* Bayi panelinde bayinin eline geçen cihaz (depo/plasiyer) her
              zaman kapalı alınır — sunucu da zorluyor (2026-08-17). Servis
              bayide merkez servisidir (#247), aşağıdaki satır. */}
          {auth?.role === "dealer" && ["Plasiyer", "Depo"].includes(operation) && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide">KOTA ALMA</span>
              <span className="text-sm font-Medium text-gray-500">
                Kota almaya kapalı olarak alınacak
              </span>
            </div>
          )}
          {auth?.role === "dealer" && operation === "Servis" && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide">HEDEF</span>
              <span className="text-sm font-Medium text-teal-700">Merkez servisi</span>
            </div>
          )}
          {auth?.role !== "dealer" && operation === "Servis" && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide">KOTA ALMA</span>
              <span className="text-sm font-Medium text-emerald-600">
                Kota almaya açık olarak alınacak
              </span>
            </div>
          )}
          {operation === "Servis" && selectedReasons.length > 0 && (
            <div className="flex items-start justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide shrink-0 pt-0.5">SERVİS NEDENİ</span>
              <span className="text-sm text-teal-700 text-right ml-4 font-medium">
                {selectedReasons.join(", ")}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-xs font-Medium text-gray-500 tracking-wide">BAĞLANTI</span>
            <span className={`text-sm font-medium ${connectDuringOperation ? "text-emerald-600" : "text-gray-500"}`}>
              {connectDuringOperation ? "Kurulacak" : "Kurulmayacak"}
            </span>
          </div>
          {description && (
            <div className="flex items-start justify-between px-4 py-3">
              <span className="text-xs font-Medium text-gray-500 tracking-wide shrink-0 pt-0.5">AÇIKLAMA</span>
              <span className="text-sm text-gray-900 text-right ml-4">{description}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-6 px-4 py-3 bg-amber-50 rounded-xl border border-amber-200">
        <FaExclamationTriangle className="w-4 h-4 text-amber-500 shrink-0" />
        <span className="text-xs text-amber-700">{config.confirmText}</span>
      </div>
    </div>
  );

  // ─── Step 3: Sonuç ───────────────────────────────────────────────────────
  const renderStep3 = () => (
    <div className="flex flex-col items-center justify-center py-12">
      {isProcessing ? (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-indigo-50 mb-6">
            <FaSpinner className="w-10 h-10 text-indigo-600 animate-spin" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">İşlem Yapılıyor</h3>
          <p className="text-sm text-gray-500">Lütfen bekleyin...</p>
        </div>
      ) : isSuccess ? (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-emerald-50 mb-6">
            <FaCheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">İşlem Başarılı</h3>
          <div className="mt-4 p-4 bg-emerald-50 rounded-xl max-w-sm mx-auto border border-emerald-100">
            <p className="text-sm text-gray-700">
              {successMessage || `"${device?.customName || device?.deviceName}" cihazı için ${config.title.toLowerCase()} işlemi başarıyla tamamlandı.`}
            </p>
          </div>
          <button
            className="mt-6 px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors shadow-sm"
            onClick={onClose}
          >
            Kapat
          </button>
        </div>
      ) : isError ? (
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-red-50 mb-6">
            <FaExclamationTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">İşlem Başarısız</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
            {errorMessage || "İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin."}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              className="px-5 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2"
              onClick={() => setCurrentStep(1)}
            >
              <FaArrowLeft className="w-3.5 h-3.5" />
              Geri Dön
            </button>
            <button
              className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-sm"
              onClick={handleRetry}
            >
              <FaRedo className="w-3.5 h-3.5" />
              Tekrar Dene
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <FaSpinner className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">Bekleniyor...</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[420px] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col bg-white`}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${config.gradient} px-5 py-4 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <IconComponent className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold tracking-wide">{config.title}</h3>
                <p className="text-xs text-white/80 mt-0.5">
                  {device?.customName || device?.deviceName} : {device?.serialNo}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                isProcessing
                  ? "bg-white/10 cursor-not-allowed opacity-40"
                  : "bg-white/10 hover:bg-white/20"
              }`}
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Adım Göstergesi — adım sayısı operasyona göre değişir */}
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => {
              const num = idx + 1;
              return (
                <React.Fragment key={step.key}>
                  {idx > 0 && (
                    <div className={`flex-1 h-0.5 mx-1.5 rounded-full transition-colors ${
                      currentStep >= num ? stepBarColor : "bg-gray-200"
                    }`} />
                  )}
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-Bold transition-all ${
                      currentStep >= num
                        ? `bg-gradient-to-br ${config.gradient} text-white shadow-sm`
                        : "bg-gray-200 text-gray-400"
                    }`}>
                      {num}
                    </div>
                    <span className={`text-[10px] mt-1 font-Medium ${
                      currentStep >= num ? "text-gray-700" : "text-gray-400"
                    }`}>{step.label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* İçerik — aktif adım */}
        <div className="flex-1 overflow-y-auto p-5">
          {stepKey === "device" && renderDeviceStep()}
          {stepKey === "playmaker" && renderPlaymakerStep()}
          {stepKey === "firm" && renderFirmStep()}
          {stepKey === "reason" && renderReasonStep()}
          {stepKey === "note" && renderNoteStep()}
          {stepKey === "confirm" && renderStep2()}
          {stepKey === "result" && renderStep3()}
          {stepError && (
            <p className="mt-4 text-sm text-red-500">{stepError}</p>
          )}
        </div>

        {/* Alt Butonlar — sonuç adımı hariç her adımda geri/ileri */}
        {stepKey !== "result" && (
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-between gap-3">
            {currentStep === 1 ? (
              <button
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-Medium rounded-xl hover:bg-gray-50 transition-colors"
                onClick={onClose}
              >
                <FaTimes className="w-3.5 h-3.5 text-gray-400" />
                İptal
              </button>
            ) : (
              <button
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-Medium rounded-xl hover:bg-gray-50 transition-colors"
                onClick={handleBack}
              >
                <FaArrowLeft className="w-3.5 h-3.5 text-gray-400" />
                Geri
              </button>
            )}

            {stepKey === "confirm" ? (
              <button
                className={`flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${config.gradient} text-white text-sm font-Medium rounded-xl shadow-sm hover:shadow-md transition-all`}
                onClick={handleConfirm}
              >
                <FaCheck className="w-3.5 h-3.5" />
                Onayla
              </button>
            ) : (
              <button
                className={`flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${config.gradient} text-white text-sm font-Medium rounded-xl shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none`}
                onClick={handleNext}
                disabled={
                  (stepKey === "playmaker" && !selectedPlaymakerId) ||
                  (stepKey === "firm" && !selectedFirmName)
                }
              >
                Devam Et
                <FaAngleRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Overlay */}
      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 z-[9998] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
          onClick={isProcessing ? undefined : onClose}
        />
      )}
    </>
  );
}

export default OperationDrawer;
